import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';

// dayjs plugins
import dayjs from 'dayjs';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isLeapYear); // dependency for isoWeeksInYear

// components
import { Popover } from 'primeng/popover';

// types
import { weekString } from '@app/core/types/week-string.type';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { Bind } from 'primeng/bind';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';

export interface IWeekRange {
  startWeek: weekString;
  endWeek: weekString;
}

@Component({
  selector: 'ag1-week-range',
  templateUrl: './ag1-week-range.component.html',
  providers: [
    {
      // add component as a value accessor to angular forms
      // this allows the component to be used as a form control
      // and to be updated by the form control
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: Ag1WeekRangeComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    Bind,
    Popover,
    NgSelectComponent,
    FormsModule,
    Button,
    Tooltip,
    AngularSvgIconModule,
    NgClass,
  ],
})
export class Ag1WeekRangeComponent
  implements OnInit, ControlValueAccessor, OnChanges
{
  @Output() startWeekChange: EventEmitter<weekString> =
    new EventEmitter<weekString>();
  @Output() endWeekChange: EventEmitter<weekString> =
    new EventEmitter<weekString>();

  // custom template
  /** Pass in a template to customize the trigger button */
  @Input() customTriggerTemplateRef?: TemplateRef<any>;

  /** Pass in a template to customize the displayed value */
  @Input() customValueTemplateRef?: TemplateRef<any>;

  /** Indicates if the week range has an error/invalid state */
  @Input() hasError: boolean = false;

  // date range
  @Input() startWeek: weekString;
  @Input() endWeek: weekString;

  // display
  // configures whether the user can select a single week or a range of weeks
  @Input() selectionType: 'range' | 'single' = 'range';
  @Input() separator: string = '-';

  // use ViewChild to access the menu component
  // in order to close the menu from outside the component
  weekRangePickerPopover = viewChild<Popover>('weekRangePicker');

  placeholder: string;
  weekSelection: 'initialRange' | 'alterRange' = 'initialRange';

  // selections
  selectedStartWeek: weekString;
  selectedEndWeek: weekString;

  currentWeekString: weekString;
  calendarYear: number;

  weeks: number[] = [];
  years: number[];

  weekFormat: string = 'GGGG[-W]WW';

  readonly RANGE_SELECTION = 'range' as const;
  readonly SINGLE_SELECTION = 'single' as const;

  constructor() {}

  ngOnInit(): void {
    this.currentWeekString = this.getCurrentWeek();
    this.calendarYear = dayjs(
      this.currentWeekString,
      this.weekFormat,
    ).isoWeekYear();
    this.placeholder = this._weekPlaceholder();
    this.years = this.generateYearOptions(this.calendarYear);
    this.generateCalendarStructure();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if selection type changes, set start and end week to start week
    // this is to avoid issues when going from range to single selection
    if (
      changes.selectionType?.previousValue === this.RANGE_SELECTION &&
      changes.selectionType?.currentValue === this.SINGLE_SELECTION
    ) {
      this.endWeek = this.startWeek;

      this.applyWeekRange();
    }

    if (
      (changes['startWeek'] || changes['endWeek']) &&
      this.startWeek &&
      this.endWeek
    ) {
      this.selectedStartWeek = this.startWeek;
      this.selectedEndWeek = this.endWeek;

      this.applyWeekRange();
    }
  }

  private _weekPlaceholder(): string {
    return this.isRangeSelection ? 'Select a week range' : 'Select a week';
  }

  getCurrentWeek(): weekString {
    return `${dayjs().isoWeekYear()}-W${dayjs()
      .isoWeek()
      .toString()
      .padStart(2, '0')}` as weekString; // eg 2025-W01
  }

  getWeeksInYear(year: number): number {
    // choose middle of year to avoid issues with leap years and ISO week year boundaries
    return dayjs(`${year}-06-01`).isoWeeksInYear();
  }

  generateYearOptions(calendarYear: number): number[] {
    const yearPadding: number = 10;
    const years: number[] = [];
    for (let i = yearPadding; i >= 0; i--) {
      years.push(calendarYear - i);
    }

    for (let i = 1; i <= yearPadding; i++) {
      years.push(calendarYear + i);
    }
    return years;
  }

  changeCurrentYear(calendarYear: number): void {
    this.calendarYear = calendarYear;
    this.generateCalendarStructure();
  }

  generateCalendarStructure(): void {
    this.weeks = [];

    // gets the number of weeks in the current year
    const weeksCount: number = this.getWeeksInYear(this.calendarYear);

    for (let i = 1; i <= weeksCount; i++) {
      this.weeks.push(i);
    }
  }

  generatePreviousYearCalendar(): void {
    this.calendarYear--;
    this.generateCalendarStructure();
  }

  generateNextYearCalendar(): void {
    this.calendarYear++;
    this.generateCalendarStructure();
  }

  getWeekString(week: number, year: number): weekString {
    // zero-pad the week number if it is less than 10
    const weekString: string =
      week < 10 ? `${year}-W0${week}` : `${year}-W${week}`;

    return weekString as weekString;
  }

  getWeekStyle(week: number): string {
    const weekString: weekString = this.getWeekString(week, this.calendarYear);

    const classes: string[] = [
      'p-1',
      'aspect-square',
      'text-center',
      'select-none',
      'cursor-pointer',
      'outline-none',
      'focus-visible:ring-2',
      'focus-visible:ring-offset-2',
      'focus-visible:ring-offset-white',
      'focus-visible:ring-french-blue-500',
    ];

    // most specific first
    // style initial selection (i.e both start and end weeks are set the same)
    if (
      this.compareWeeks(this.startWeek, weekString) &&
      this.compareWeeks(this.endWeek, weekString)
    ) {
      classes.push(
        'bg-french-blue-500',
        'text-white',
        'rounded',
        'hover:bg-french-blue-600',
      );
    } else if (this.styleSelectedEndWeek(weekString)) {
      // style end week
      classes.push(
        'bg-french-blue-500',
        'text-white',
        'rounded-r',
        'hover:bg-french-blue-600',
      );
    } else if (this.styleSelectedStartWeek(weekString)) {
      // style start week
      classes.push(
        'bg-french-blue-500',
        'text-white',
        'rounded-l',
        'hover:bg-french-blue-600',
      );
    } else if (this.styleSelectedRange(weekString)) {
      // style weeks in range
      classes.push('bg-french-blue-100', 'hover:bg-french-blue-200');
    } else if (this.styleCurrentWeek(weekString)) {
      // style current week
      classes.push(
        'rounded-full bg-white text-french-blue-500 border border-french-blue-500 hover:bg-slate-100',
      );
    } else {
      // style other weeks
      classes.push('rounded hover:bg-slate-100');
    }

    return classes.join(' ');
  }

  compareWeeks(week1String: weekString, week2String: weekString): boolean {
    if (!week1String || !week2String) return false;

    // no need to use dayjs as we are comparing strings
    return week1String === week2String;
  }

  styleCurrentWeek(weekString: weekString): boolean {
    return this.compareWeeks(weekString, this.currentWeekString);
  }

  styleSelectedStartWeek(weekString: weekString): boolean {
    if (!this.startWeek) return false;

    return this.compareWeeks(weekString, this.startWeek);
  }

  styleSelectedEndWeek(weekString: weekString): boolean {
    if (!this.endWeek) return false;

    return this.compareWeeks(weekString, this.endWeek);
  }

  styleSelectedRange(weekString: weekString): boolean {
    if (!this.startWeek || !this.endWeek) return false;

    const { year: year, week: week } = this.splitWeekString(weekString);
    const { year: startYear, week: startWeek } = this.splitWeekString(
      this.startWeek,
    );
    const { year: endYear, week: endWeek } = this.splitWeekString(this.endWeek);

    // eg. Test case 2025-W52 to 2026-W53
    const isAfterStartWeek: boolean =
      year > startYear || (year === startYear && week > startWeek);
    const isBeforeEndWeek: boolean =
      year < endYear || (year === endYear && week < endWeek);

    return isAfterStartWeek && isBeforeEndWeek;
  }

  selectWeek(week: { week: number; year: number }): void {
    const selectedWeekString: weekString = this.getWeekString(
      week.week,
      week.year,
    );

    if (this.isSingleSelection) {
      this.setStartWeek(selectedWeekString);
      this.setEndWeek(selectedWeekString);
      this.weekSelection = 'initialRange';

      this.applyWeekRange();
      return;
    }

    if (this.weekSelection === 'initialRange') {
      // initial selection
      this.setStartWeek(selectedWeekString);
      this.setEndWeek(selectedWeekString);
      this.weekSelection = 'alterRange';

      // update the UI
      this.displayWeekRange(this.startWeek, this.endWeek);
    } else {
      // second selection alters the range
      // but because a user can select weeks in any orders

      // Only use the initial selection and the new selection
      const weeks: weekString[] = [this.startWeek, selectedWeekString];

      this.orderAndSetWeeks(weeks);
      this.weekSelection = 'initialRange';

      this.applyWeekRange();
    }
  }

  setStartWeek(weekString: weekString) {
    this.startWeek = weekString;
  }

  setEndWeek(weekString: weekString) {
    this.endWeek = weekString;
  }

  splitWeekString(weekString: weekString): { year: number; week: number } {
    const year: number = parseInt(weekString.split('-W')[0]);
    const week: number = parseInt(weekString.split('-W')[1]);
    return { year, week };
  }

  orderAndSetWeeks(weekStrings: weekString[]): void {
    weekStrings.sort((a: weekString, b: weekString) => {
      const { year: yearA, week: weekA } = this.splitWeekString(a);
      const { year: yearB, week: weekB } = this.splitWeekString(b);

      // Compare years first
      if (yearA !== yearB) {
        return yearA - yearB;
      }

      // If years are the same, compare week numbers
      return weekA - weekB;
    });

    this.setStartWeek(weekStrings[0]);
    this.setEndWeek(weekStrings[weekStrings.length - 1]);
  }

  emitWeekRangeSelection(): void {
    this.startWeekChange.emit(this.startWeek);
    this.endWeekChange.emit(this.endWeek);
  }

  displayWeekRange(
    startWeekString: weekString,
    endWeekString: weekString,
  ): void {
    if (!startWeekString || !endWeekString) {
      return;
    }

    this.selectedStartWeek = startWeekString;
    this.selectedEndWeek = endWeekString;
  }

  clearWeeks(): void {
    // reset values
    this.startWeek = null;
    this.endWeek = null;

    // reset selections
    this.weekSelection = 'initialRange';

    this.applyWeekRange();
  }

  closeMenu(): void {
    this.weekRangePickerPopover()?.hide();
  }

  handleTriggerClick(event: Event): void {
    event.stopImmediatePropagation();

    if (!(this.disabled || this.readonly)) {
      this.weekRangePickerPopover()?.toggle(event);
    }
  }

  applyWeekRange(): void {
    this.markAsTouched();

    if (!this.disabled) {
      // inform parent component of week changes
      this.emitWeekRangeSelection();

      // update form control
      this.onChange(this.getValue());

      // update the UI
      this.displayWeekRange(this.startWeek, this.endWeek);
    }

    this.closeMenu();
  }

  getValue(): { startWeek: weekString; endWeek: weekString } | null {
    if (!this.startWeek || !this.endWeek) {
      return null;
    }

    return {
      startWeek: this.startWeek,
      endWeek: this.endWeek,
    };
  }

  get isRangeSelection(): boolean {
    return this.selectionType === this.RANGE_SELECTION;
  }

  get isSingleSelection(): boolean {
    return this.selectionType === this.SINGLE_SELECTION;
  }

  // required by ControlValueAccessor

  // ControlValueAccessor methods
  // used to update the form control
  onChange: (value: IWeekRange | null) => void = (value) => {};

  // used to inform the form control that the user has interacted with the component
  onTouched: () => void = () => {};

  // used to set the value using the form control
  writeValue(value: IWeekRange | null): void {
    if (value) {
      if (this.isRangeSelection) {
        this.startWeek = value.startWeek;
        this.endWeek = value.endWeek;
      } else {
        this.startWeek = value.startWeek;
        this.endWeek = value.startWeek;
      }

      // update the UI
      this.displayWeekRange(this.startWeek, this.endWeek);
    } else {
      this.clearInput();
    }
  }

  // used to register the onChange function
  registerOnChange(onChange: (value: IWeekRange | null) => void): void {
    this.onChange = onChange;
  }

  // used to register the onTouched function
  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  // used to set the disabled state using the form control
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  clearInput(): void {
    this.startWeek = null;
    this.endWeek = null;
    this.selectedStartWeek = null;
    this.selectedEndWeek = null;
  }

  // used to inform the form control that the user has interacted with the component
  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  // ControlValueAccessor properties
  // used to track the state of the form control
  touched: boolean = false;
  disabled: boolean = false;
  @Input() readonly: boolean = false;

  get hasValue(): boolean {
    return !!this.startWeek && !!this.endWeek;
  }
}
